/**
 * Irmão: `src/app/components/filters/FilterSection.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 */

import { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  REVISAO_ACCENT_TILE,
  REVISAO_FILTER_FOCUS_RING,
  RevisaoSectionAccent,
  revisaoFilterCx,
} from "./tokens";

export interface RevisaoFilterSectionProps {
  id: string;
  /** Step number shown before the title. Omit when the section is not a numbered step. */
  step?: number;
  icon: ReactNode;
  accent: RevisaoSectionAccent;
  title: string;
  description?: string;
  /** Search slot. The section does not implement search; it only reserves the space. */
  search?: ReactNode;
  /** Overflow slot (excedente), right-aligned in the header. Sister prop name: headerRight. */
  headerRight?: ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggleOpen?: () => void;
  selectedCount?: number;
  children: ReactNode;
}

/**
 * One filter category. The section accent lives in the icon tile and nowhere
 * else — it must not travel into the options below it.
 */
export default function RevisaoFilterSection({
  id,
  step,
  icon,
  accent,
  title,
  description,
  search,
  headerRight,
  collapsible = false,
  open = true,
  onToggleOpen,
  selectedCount = 0,
  children,
}: RevisaoFilterSectionProps): JSX.Element {
  const titleId = `${id}-title`;
  const expanded = collapsible ? open : true;

  const heading = (
    <div className="flex min-w-0 items-start gap-3">
      <span
        className={revisaoFilterCx(
          "grid h-9 w-9 shrink-0 place-items-center rounded-[10px]",
          "[&>svg]:h-5 [&>svg]:w-5",
          REVISAO_ACCENT_TILE[accent],
        )}
        aria-hidden="true"
      >
        {icon}
      </span>

      <div className="min-w-0">
        <h3 id={titleId} className="flex items-center gap-2 text-[15px] font-semibold text-ink">
          {step !== undefined ? (
            <span className="tabular-nums text-ink-muted" aria-hidden="true">
              {step}
            </span>
          ) : null}
          <span className="truncate">{title}</span>
          {collapsible && selectedCount > 0 ? (
            <span className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold text-select-ink">
              {selectedCount}
            </span>
          ) : null}
        </h3>
        {description ? (
          <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>
        ) : null}
      </div>
    </div>
  );

  return (
    <section
      id={id}
      aria-labelledby={titleId}
      className="scroll-mt-4 rounded-card border border-edge bg-surface p-4 shadow-panel sm:p-5"
    >
      <div className="revisao-filter-section-header flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggleOpen}
            aria-expanded={expanded}
            aria-controls={`${id}-content`}
            className={revisaoFilterCx(
              "flex w-full items-start justify-between gap-3 rounded-chip text-left",
              REVISAO_FILTER_FOCUS_RING,
            )}
          >
            {heading}
            <ChevronDown
              className={revisaoFilterCx(
                "mt-2 h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150",
                expanded && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          heading
        )}

        {expanded && (search || headerRight) ? (
          <div className="revisao-filter-section-tools flex min-w-0 shrink-0 flex-wrap items-center gap-3 lg:pt-0.5">
            {search ? (
              <div className="revisao-filter-section-search w-full min-w-0 lg:w-80">{search}</div>
            ) : null}
            {headerRight}
          </div>
        ) : null}
      </div>

      {expanded ? (
        <div id={`${id}-content`} className="mt-4">
          {children}
        </div>
      ) : null}
    </section>
  );
}
